/**
 * Export — of what you are looking at.
 *
 * The old control exported a date range from whichever of the two
 * pre-unification endpoints a radio picked, which had nothing to do with the
 * list on screen. It now takes the exact query string the list built and calls
 * the same endpoint with `paginate=false`, so "Export" means "these rows".
 *
 * The modal is a confirmation, not a form: it names the filters, states the row
 * count, and offers one escape hatch — ignore the filters and export a date
 * range — which is the old behaviour rebuilt on the unified endpoint.
 */

import React, { Fragment, useState } from 'react';
import { DatePicker, Modal, ConfigProvider } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import useAxiosClient from '../../axios-client';
import { useStateContext } from '../../contexts/ContextProvider';
import { toast } from 'react-toastify';
import { OrderStatusEnum } from '../../utils/enums/OrderStatusEnum';
import { OrderTypeEnum } from '../../utils/enums/OrderTypeEnum';
import { PRIMARY, hexToRgba, SHELL_THEME as THEME, shellAntdTheme } from '../../shellTokens';

const MAX_RANGE_MONTHS = 2;

// Column key → header label and value reader. The export follows the list's
// visible columns, so hiding Team on screen hides it in the file too.
const FIELDS = {
  id: { header: 'Order ID', value: (o) => o.id },
  channel: { header: 'Channel', value: (o) => (o.orderType === OrderTypeEnum.MERCHANT ? 'Offline' : o.marketplace || 'Online') },
  orderedBy: { header: 'Ordered By', value: (o) => o.orderedBy },
  team: { header: 'Team', value: (o) => o.team || '' },
  createdBy: { header: 'Submitted By', value: (o) => o.createdBy },
  createdAt: { header: 'Order Date', value: (o) => (o.createdAt ? dayjs(o.createdAt).format('DD-MM-YYYY') : '') },
  deliveryDate: { header: 'Delivery Date', value: (o) => (o.deliveryDate ? dayjs(o.deliveryDate).format('DD-MM-YYYY') : '') },
  totalAmount: { header: 'Total Amount', value: (o) => Number(o.totalAmount) || 0 },
  status: { header: 'Status', value: (o) => (OrderStatusEnum.find((s) => s.value === o.status)?.label ?? 'Unknown') },
};
// Fallback for a caller that does not pass a column set (or hides everything).
const DEFAULT_KEYS = ['id', 'channel', 'orderedBy', 'createdBy', 'createdAt', 'deliveryDate', 'totalAmount', 'status'];

const ExportOrderExcel = ({ query = '', total = 0, filters = [], columns = [] }) => {
  const axiosClient = useAxiosClient();
  const { user, darkMode } = useStateContext();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // false = the current view; true = the date-range escape hatch.
  const [ignoreFilters, setIgnoreFilters] = useState(false);
  const [dates, setDates] = useState(null);

  const keys = columns.length
    ? columns.map((c) => c.key).filter((k) => FIELDS[k])
    : DEFAULT_KEYS;
  const exportKeys = keys.length ? keys : DEFAULT_KEYS;

  const close = () => { setOpen(false); setIgnoreFilters(false); setDates(null); };

  const run = async () => {
    let requestQuery;
    let filenameSuffix;

    if (ignoreFilters) {
      if (!dates || !dates[0] || !dates[1]) {
        toast.error('Please select a date range');
        return;
      }
      if (dates[1].diff(dates[0], 'month') > MAX_RANGE_MONTHS) {
        toast.error(`Please select a date range of up to ${MAX_RANGE_MONTHS} months.`);
        return;
      }
      // Order date, not delivery — same as the control this replaces.
      requestQuery = `orderDateStart=${dates[0].format('YYYY-MM-DD')}&orderDateEnd=${dates[1].format('YYYY-MM-DD')}`;
      filenameSuffix = `${dates[0].format('YYYYMMDD')}-${dates[1].format('YYYYMMDD')}`;
    } else {
      requestQuery = query;
      filenameSuffix = dayjs().format('YYYYMMDD');
    }

    setLoading(true);
    try {
      const url = `/orders/getOrders/${user.id}?${[requestQuery, 'paginate=false'].filter(Boolean).join('&')}`;
      const res = await axiosClient.get(url);
      const rows = res.data?.data || [];
      if (rows.length === 0) {
        toast.error('No orders match — nothing to export.');
        return;
      }
      const sheetRows = rows.map((o) => Object.fromEntries(
        exportKeys.map((k) => [FIELDS[k].header, FIELDS[k].value(o)]),
      ));
      const worksheet = XLSX.utils.json_to_sheet(sheetRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');
      XLSX.writeFile(workbook, `Orders_${filenameSuffix}.xlsx`);
      close();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  const disabledDate = (current) => current && current > dayjs().endOf('day');

  const t = THEME[darkMode ? 'dark' : 'light'];
  const optionStyle = (on) => ({
    border: `1px solid ${on ? PRIMARY : t.border}`,
    background: on ? hexToRgba(PRIMARY, .06) : 'transparent',
    borderRadius: 11,
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    cursor: 'pointer',
    color: t.text,
  });
  const radio = (on) => ({
    width: 15, height: 15, borderRadius: 8, flexShrink: 0,
    background: on ? PRIMARY : 'transparent',
    border: on ? 'none' : `1.5px solid ${t.borderStrong}`,
    color: '#fff', fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
  });

  return (
    <Fragment>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Export the orders currently listed"
        style={{
          height: 38, padding: '0 14px', borderRadius: 9, border: '1px solid var(--border)',
          background: 'var(--surface)', color: 'var(--text)', font: '600 13px Outfit, sans-serif',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
        }}
      >
        <DownloadOutlined style={{ color: 'var(--muted)', fontSize: 12 }} />
        Export
        <span style={{ font: '600 11px var(--mono, monospace)', color: 'var(--dim)', padding: '2px 6px', borderRadius: 5, background: 'var(--surface2)' }}>
          {total}
        </span>
      </button>

      <ConfigProvider theme={shellAntdTheme(darkMode)}>
        <Modal
          open={open}
          title="Export orders"
          width={440}
          onCancel={close}
          confirmLoading={loading}
          onOk={run}
          okText={ignoreFilters ? 'Export date range' : `Export ${total} order${total === 1 ? '' : 's'}`}
          okButtonProps={{ disabled: !ignoreFilters && total === 0 }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 6 }}>

            <div onClick={() => setIgnoreFilters(false)} style={optionStyle(!ignoreFilters)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <span style={radio(!ignoreFilters)}>{!ignoreFilters && '✓'}</span>
                <span style={{ font: '600 13px Outfit, sans-serif' }}>Current view</span>
                <span style={{ marginLeft: 'auto', font: '700 12px var(--mono, monospace)', color: t.activeText }}>
                  {total} order{total === 1 ? '' : 's'}
                </span>
              </div>
              {filters.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingLeft: 24 }}>
                  {filters.map((f) => (
                    <span key={f} style={{ font: '600 11px Outfit, sans-serif', color: t.muted, background: t.surface2, border: `1px solid ${t.border}`, padding: '2px 7px', borderRadius: 6 }}>
                      {f}
                    </span>
                  ))}
                </div>
              )}
              <div style={{ paddingLeft: 24, font: '400 11.5px Outfit, sans-serif', color: t.dim }}>
                {filters.length === 0 && 'No filters applied. '}
                {exportKeys.length} column{exportKeys.length === 1 ? '' : 's'} · <span style={{ fontFamily: 'var(--mono, monospace)' }}>/orders/getOrders</span>
              </div>
            </div>

            <div onClick={() => setIgnoreFilters(true)} style={optionStyle(ignoreFilters)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <span style={radio(ignoreFilters)}>{ignoreFilters && '✓'}</span>
                <span style={{ font: '600 13px Outfit, sans-serif', color: ignoreFilters ? t.text : t.muted }}>
                  Ignore filters — export a date range
                </span>
              </div>
              <div style={{ paddingLeft: 24 }} onClick={(e) => e.stopPropagation()}>
                <DatePicker.RangePicker
                  format="DD/MM/YYYY"
                  value={dates}
                  onChange={(d) => { setDates(d); setIgnoreFilters(true); }}
                  disabledDate={disabledDate}
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ paddingLeft: 24, font: '400 11.5px Outfit, sans-serif', color: t.dim }}>
                Max {MAX_RANGE_MONTHS} months. Same endpoint; order date, not delivery.
              </div>
            </div>

          </div>
        </Modal>
      </ConfigProvider>
    </Fragment>
  );
};

export default ExportOrderExcel;
